import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { QimaOptionalType } from '@qima/ngx-qima';
import { BehaviorSubject, Observable } from 'rxjs';
import { GlobalEnvironmentService } from '../core/services/global-environment.service';

export interface ILanguage {
  code: string;
  name: string; // Language name (native for translation, English for order preferences)
  flag: string;
}

export interface TranslationParams {
  [key: string]: string | number | boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app_language';
  private readonly DEFAULT_LANGUAGE = 'en';

  public currentLanguage = signal<string>(this.DEFAULT_LANGUAGE);
  public currentLanguageInfo = computed(
    () => this.translationLanguages.find(lang => lang.code === this.currentLanguage()) ?? this.translationLanguages[0]
  );

  /**
   * Languages available for app UI translation (languages with translation files in assets/i18n/)
   * Used by: Header language selector dropdown
   * Names are in native language for better UX
   */
  public readonly translationLanguages: ILanguage[] = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
  ];

  /**
   * Languages available for order preferences (auditor language requirements)
   * Used by: Multi-language-select component for order preferences
   * Names are in English for consistency with backend API
   */
  private _orderPreferenceLanguages: ILanguage[] = [];
  private _languageDataLoaded$ = new BehaviorSubject<boolean>(false);
  constructor(
    private readonly translateService: TranslateService,
    private readonly http: HttpClient,
    private readonly globalEnvironmentService: GlobalEnvironmentService
  ) {
    this.initializeLanguage();
    this._loadOrderPreferenceLanguages();
  }

  private _loadOrderPreferenceLanguages(): void {
    const apiUrl = this.globalEnvironmentService.getPublicApiUrl();
    this.http
      .get<{
        content: Array<{ value: string; text: string }>;
      }>(`${apiUrl}/parameter/v2/constant-mapping/CERTIFICATION_LANGUAGE_PREFERENCES`)
      .subscribe({
        next: response => {
          if (response?.content) {
            this._orderPreferenceLanguages = response.content.map(param => ({
              code: param.value, // ISO code
              name: param.text, // Display name
              flag: '🌐', // Default flag since flags are not displayed in order preferences
            }));
            this._languageDataLoaded$.next(true);
          }
        },
        error: error => {
          console.error('Error loading language preferences:', error);
          // Still emit true to prevent infinite waiting
          this._languageDataLoaded$.next(true);
        },
      });
  }

  public get orderPreferenceLanguages(): ILanguage[] {
    return this._orderPreferenceLanguages;
  }

  /**
   * Observable that emits true when language data has been loaded from the API
   * Used to ensure language data is available before displaying preferences
   */
  public get languageDataLoaded$(): Observable<boolean> {
    return this._languageDataLoaded$.asObservable();
  }

  private initializeLanguage(): void {
    this.translateService.setDefaultLang(this.DEFAULT_LANGUAGE);
    this.preloadLanguages();

    const savedLanguage = this.getSavedLanguage();
    const browserLanguage = this.getBrowserLanguage();
    const validSavedLanguage = this.getValidLanguage(savedLanguage);
    const validBrowserLanguage = this.getValidLanguage(browserLanguage);
    const initialLanguage = validSavedLanguage ?? validBrowserLanguage ?? this.DEFAULT_LANGUAGE;

    this.translateService.use(initialLanguage);
    this.currentLanguage.set(initialLanguage);
  }

  private preloadLanguages(): void {
    // Preload languages by getting a common translation key
    this.translationLanguages.forEach(lang => {
      this.translateService.get('common.language', { lang: lang.code }).subscribe();
    });
  }

  private getValidLanguage(language: QimaOptionalType<string>): string | null {
    return this.isValidLanguage(language) ? language : null;
  }

  private getSavedLanguage(): QimaOptionalType<string> {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private getBrowserLanguage(): QimaOptionalType<string> {
    const browserLang = navigator.language?.split('-')[0];

    if (this.isValidLanguage(browserLang)) {
      return this.isLanguageSupported(browserLang) ? browserLang : this.DEFAULT_LANGUAGE;
    }

    return null;
  }

  private isValidLanguage(language: QimaOptionalType<string>): language is string {
    return (
      language !== null &&
      language !== undefined &&
      language !== '' &&
      language.trim() !== '' &&
      language.length > 0 &&
      language !== 'null' &&
      language !== 'undefined'
    );
  }

  private isLanguageSupported(language: string): boolean {
    return this.translationLanguages.some(lang => lang.code === language);
  }

  public setLanguage(language: string): void {
    if (!this.isLanguageSupported(language)) {
      language = this.DEFAULT_LANGUAGE;
    }

    // Note: reloadLang method doesn't exist on TranslateService
    // We'll use use() directly
    this.currentLanguage.set(language);
    this.translateService.use(language);

    try {
      localStorage.setItem(this.STORAGE_KEY, language);
    } catch (error) {
      console.warn('Could not save language to localStorage:', error);
    }
  }

  public getCurrentLanguage(): string {
    return this.currentLanguage();
  }

  public toggleLanguage(): void {
    const currentLang = this.currentLanguage();
    const currentIndex = this.translationLanguages.findIndex(lang => lang.code === currentLang);
    const nextIndex = (currentIndex + 1) % this.translationLanguages.length;
    const nextLanguage = this.translationLanguages[nextIndex];

    this.setLanguage(nextLanguage.code);
  }

  public getTranslation(key: string, params?: TranslationParams): Observable<string> {
    return this.translateService.get(key, params) as Observable<string>;
  }

  public getInstantTranslation(key: string, params?: TranslationParams): string {
    return this.translateService.instant(key, params) as string;
  }

  /**
   * Gets the full language name from an ISO 639-1 code.
   * Used for converting database codes to display names.
   * @param code - The ISO 639-1 code (e.g., 'en', 'fr')
   * @returns The full language name, or the code itself if not found
   */
  public getNameByCode(code: string): string {
    if (!code || code.trim() === '') {
      return code;
    }

    const language = this._orderPreferenceLanguages.find(lang => lang.code === code.trim().toLowerCase());
    return language ? language.name : code;
  }

  /**
   * Gets the ISO 639-1 code from a full language name.
   * Used for converting display names to database codes.
   * @param name - The full language name (e.g., 'English', 'French')
   * @returns The ISO 639-1 code, or the name itself if not found
   */
  public getCodeByName(name: string): string {
    if (!name || name.trim() === '') {
      return name;
    }

    const language = this.orderPreferenceLanguages.find(lang => lang.name.toLowerCase() === name.trim().toLowerCase());
    return language ? language.code : name;
  }

  /**
   * Converts an array of ISO codes to an array of language names.
   * Used for displaying languages from API responses.
   * @param codes - Array of ISO 639-1 codes
   * @returns Array of full language names
   */
  public convertCodesToNames(codes: string[]): string[] {
    if (!codes || codes.length === 0) {
      return [];
    }

    return codes.map(code => this.getNameByCode(code));
  }

  /**
   * Converts an array of language names to an array of ISO codes.
   * Used for sending languages to the API.
   * @param names - Array of full language names
   * @returns Array of ISO 639-1 codes
   */
  public convertNamesToCodes(names: string[]): string[] {
    if (!names || names.length === 0) {
      return [];
    }

    return names.map(name => this.getCodeByName(name));
  }
}
